import { memo, useState } from 'react';
import { Handle, Position, NodeResizer, useReactFlow } from 'reactflow';
import { ChevronDown, ChevronRight, Repeat, PlayCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { type NodeProps } from 'reactflow';

export const LoopNode = ({ id, data, selected }: NodeProps) => {
    const [collapsed, setCollapsed] = useState(false);
    const { setNodes } = useReactFlow();

    const handleToggleCollapse = (e: React.MouseEvent) => {
        e.stopPropagation();
        const newCollapsed = !collapsed;
        setCollapsed(newCollapsed);

        setNodes((nodes) => {
            const nodesWithUpdatedContainer = nodes.map((n) => {
                if (n.id === id) {
                    let newStyle = { ...n.style };
                    let newData = { ...n.data, isCollapsed: newCollapsed };

                    if (newCollapsed) {
                        newData.preCollapseSize = { width: n.style?.width, height: n.style?.height };
                        newStyle = { ...newStyle, width: 220, height: 60 };
                    } else {
                        const saved = n.data.preCollapseSize;
                        if (saved) {
                            newStyle = { ...newStyle, width: saved.width, height: saved.height };
                        } else {
                            newStyle.width = undefined; // Use CSS defaults
                            newStyle.height = undefined;
                        }
                    }

                    return { ...n, data: newData, style: newStyle };
                }
                return n;
            });

            return nodesWithUpdatedContainer.map(n => {
                if (n.parentNode === id) return { ...n, hidden: newCollapsed };
                return n;
            });
        });
    };

    return (
        <div className={cn(
            "rounded-lg border-2 transition-all group relative",
            selected ? "border-cyan-500 shadow-lg shadow-cyan-500/20 bg-cyan-50/10" : "border-cyan-300 border-dashed hover:border-cyan-400 bg-cyan-50/5",
            data?.isDisabled ? "opacity-70 bg-gray-100/50" : "",
            collapsed ? "w-[220px] h-[60px] bg-cyan-50" : "min-w-[300px] min-h-[300px] h-full w-full"
        )}>
            {!collapsed && (
                <NodeResizer
                    minWidth={200}
                    minHeight={200}
                    isVisible={selected}
                    lineClassName="border-cyan-400"
                    handleClassName="h-4 w-4 bg-cyan-500 border-2 border-white rounded shadow-sm"
                    handleStyle={{ zIndex: 100 }}
                />
            )}

            {/* Header */}
            <div className={cn(
                "flex items-center justify-between px-3 py-2 border-b transition-colors rounded-t-lg backdrop-blur-sm",
                selected ? "bg-cyan-100/80 border-cyan-200" : "bg-cyan-50/80 border-cyan-200"
            )}>
                <div className="flex items-center gap-2 overflow-hidden">
                    <button onClick={handleToggleCollapse} className="hover:bg-cyan-200 rounded p-0.5 text-cyan-700 transition-colors">
                        {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                    </button>
                    <Repeat className="h-4 w-4 text-cyan-600" />
                    <div className="flex flex-col">
                        <span className="text-sm font-bold text-cyan-900 truncate max-w-[140px]" title={data?.label}>
                            {data?.label || "Iterate Loop"}
                        </span>
                        {data?.isDevMode && (
                            <span className="text-[9px] text-orange-600 font-mono bg-orange-100 px-1 rounded w-fit">Dev Mode: {data?.sampleCount || 5}</span>
                        )}
                        {data?.rowsAffected !== undefined && (
                            <span className="text-[9px] text-green-600 font-mono bg-green-100 px-1 rounded w-fit">Processed: {data?.rowsAffected}</span>
                        )}
                        {data?.maxConcurrency > 1 && (
                            <span className="text-[9px] text-blue-600 font-mono bg-blue-100 px-1 rounded w-fit">⚡ {data.maxConcurrency}x parallel</span>
                        )}
                    </div>
                </div>

                <div className="flex items-center gap-1">
                    {/* Status Indicator (Mockup for Heatmap Logic) */}
                    {data?.executionStatus === 'Running' && <PlayCircle className="h-3 w-3 text-green-500 animate-spin" />}
                </div>
            </div>

            {/* TARGET HANDLES (Input) */}
            <Handle type="target" id="in-top" position={Position.Top} className="!w-3 !h-3 !bg-cyan-500 border-2 border-white" style={{ left: '30%' }} />
            <Handle type="target" id="in-bottom" position={Position.Bottom} className="!w-3 !h-3 !bg-cyan-500 border-2 border-white" style={{ left: '30%' }} />
            <Handle type="target" id="in" position={Position.Left} className="!w-3 !h-3 !bg-cyan-500 border-2 border-white" />

            {/* SOURCE HANDLES (Output) */}
            <Handle type="source" id="out-top" position={Position.Top} className="!w-3 !h-3 !bg-cyan-500 border-2 border-white" style={{ left: '70%' }} />
            <Handle type="source" id="out-bottom" position={Position.Bottom} className="!w-3 !h-3 !bg-cyan-500 border-2 border-white" style={{ left: '70%' }} />
            <Handle type="source" id="out" position={Position.Right} className="!w-3 !h-3 !bg-cyan-500 border-2 border-white" />

            {/* Content Area */}
            {!collapsed && !data?.hasChildren && (
                <div className="p-4 text-xs text-cyan-500 italic text-center mt-10 pointer-events-none">
                    🔁 <strong>Iterate Loop</strong> — runs inner nodes once per row/item
                    <div className="mt-2 text-[10px] opacity-70">
                        Use <code className="bg-cyan-100 px-1 rounded">{"{{item.COL}}"}</code>,{' '}
                        <code className="bg-cyan-100 px-1 rounded">{"{{index}}"}</code>,{' '}
                        <code className="bg-cyan-100 px-1 rounded">{"{{total}}"}</code> in child node configs
                    </div>
                </div>
            )}
        </div>
    );
};

LoopNode.displayName = 'LoopNode';
export default memo(LoopNode);
